import { ref } from "vue"
import { Nullable } from "src/types"

export interface ReactiveDataState<T> {
  readonly getData: () => Nullable<T> // for backwards compatibility
  readonly getError: () => Nullable<Error> // for backwards compatibility
  readonly getErrorMessage: () => Nullable<string> // for backwards compatibility
  readonly isNil: () => boolean
  readonly isLoading: () => boolean
  readonly isFailure: () => boolean
  readonly isSuccess: () => boolean
  readonly error?: { cause: Error; message: string }
  readonly data?: T
}

export function useStateFlow<T>() {
  const result = ref<ReactiveDataState<T>>({
    isNil: () => true,
    isLoading: () => false,
    isFailure: () => false,
    isSuccess: () => false,
    getData: () => null,
    getError: () => null,
    getErrorMessage: () => null
  })

  function setNilResult(): void {
    result.value = {
      isNil: () => true,
      isLoading: () => false,
      isFailure: () => false,
      isSuccess: () => false,
      getData: () => null,
      getError: () => null,
      getErrorMessage: () => null
    }
  }

  /**
   * Changes the state of this result to be loading.
   */
  function setLoadingResult(): void {
    result.value = {
      isNil: () => false,
      isLoading: () => true,
      isFailure: () => false,
      isSuccess: () => false,
      getData: () => null,
      getError: () => null,
      getErrorMessage: () => null
    }
  }

  /**
   * Changes the state of this result to be failure.
   *
   * @param ex the exception cause for failure
   */
  function setFailureResult(ex: any): void {
    let message: string = "An unexpected error occurred, please try again!"

    if (ex && ex.message) {
      message = ex.message
    }

    result.value = {
      isNil: () => false,
      isLoading: () => false,
      isFailure: () => true,
      isSuccess: () => false,
      getData: () => null,
      getError: () => ex,
      getErrorMessage: () => message,
      error: {
        cause: ex,
        message: message
      }
    }
  }

  /**
   * Changes the state of this result to be successful.
   *
   * @param data the data of the result
   */
  function setSuccessResult(data: Nullable<any>): void {
    result.value = {
      isNil: () => false,
      isLoading: () => false,
      isFailure: () => false,
      isSuccess: () => true,
      getData: () => data,
      getError: () => null,
      getErrorMessage: () => null,
      data: data
    }
  }

  return {
    result,
    setNilResult,
    setLoadingResult,
    setFailureResult,
    setSuccessResult
  }
}
